import { ref, watch } from 'vue';

/**
 * Asks the bucket or custom list inspector to show one of its sections.
 *
 * The counts that ask for this are not inside the inspector. One is on the
 * search card. The other is on the inspector's heading, which the split pane
 * and the overlay draw separately from the inspector body. So the request goes
 * through this module instead of through props.
 *
 * A click on a card's count leaves a request here. The click then carries on to
 * the card, and the card opens the inspector. The new inspector takes the
 * request while it is being created. An inspector that is already open on that
 * record sees the request arrive and switches to the section.
 *
 * Whichever inspector acts on a request uses it up, so a request never lands a
 * second time. Every click makes a new request, even for the section asked for
 * last time. So a second click on the track count still works after the reader
 * has moved to Details.
 *
 * A request can also name one row of the section to point at. A schedule
 * clicked on the schedule search opens its list on Schedules, with that
 * schedule highlighted.
 */
export type PlaylistContainerSectionRequest = {
    entityId: string;
    recordId: string;
    section: string;
    /** The id of the row in the section to highlight, such as a schedule id. */
    highlightId: string | null;
    requestedAtMs: number;
};

// A request that no inspector has taken after this long was for a click that
// opened nothing. It must not land on a record opened much later. The limit is
// generous because the drawer's inspector only mounts after its record loads.
const REQUEST_LIFETIME_MS = 10_000;

const pending = ref<PlaylistContainerSectionRequest | null>(null);

export function requestPlaylistContainerSection(entityId: string, recordId: unknown, section: string, highlightId: unknown = null): void {
    pending.value = {
        entityId,
        recordId: String(recordId),
        section,
        highlightId: highlightId === null || highlightId === undefined ? null : String(highlightId),
        requestedAtMs: Date.now(),
    };
}

/**
 * Drops the waiting request, if there is one. The search page calls it when a click on a card only
 * changes the selection, so a request made by a count in that card never lands on a later click.
 */
export function dropPlaylistContainerRequest(): void {
    pending.value = null;
}

/** The request waiting for this record, if there is one. Taking it uses it up. */
export function takePlaylistContainerRequest(entityId: string, recordId: unknown): PlaylistContainerSectionRequest | null {
    const request = pending.value;
    if (request === null || request.entityId !== entityId || request.recordId !== String(recordId)) {
        return null;
    }
    if (Date.now() - request.requestedAtMs > REQUEST_LIFETIME_MS) {
        return null;
    }
    pending.value = null;
    return request;
}

/** The section asked for this record, if a request is waiting. Taking it uses it up. */
export function takePlaylistContainerSection(entityId: string, recordId: unknown): string | null {
    return takePlaylistContainerRequest(entityId, recordId)?.section ?? null;
}

/** Calls `onRequest` with each request for this record while the caller is mounted. */
export function watchPlaylistContainerSection(
    entityId: () => string,
    recordId: () => unknown,
    onRequest: (section: string, highlightId: string | null) => void,
): void {
    watch(pending, () => {
        const request = takePlaylistContainerRequest(entityId(), recordId());
        if (request !== null) onRequest(request.section, request.highlightId);
    });
}
